"use client";

import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Eye, EyeOff, Edit3, Trash2, Loader2, Check, X, AlertTriangle, Upload, ImageIcon,
} from "lucide-react";
import { useAdmin } from "@/hooks/use-admin";
import { useAuth } from "@/contexts/auth-context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import Image from "next/image";
import { uploadFile } from "@/services/storage";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { ArtistInput } from "@/components/profile/artist-input";
import MultiGenreInput from "@/components/ui/multi-genre-input";
import { AIDisclosureEditor } from "@/components/media/ai-disclosure-editor";
import { YearPickerInput } from "@/components/ui/year-picker-input";
import { DbSuggestInput, type SuggestSource } from "@/components/ui/db-suggest-input";

export interface AdminField {
  key: string;
  label: string;
  type: "text" | "textarea" | "select" | "number" | "image" | "artist" | "genre" | "ai_disclosure" | "year" | "date" | "suggest";
  options?: { value: string; label: string }[];
  /** type="suggest": where existing values come from (distinct values of these fields). */
  suggest?: SuggestSource[];
  /** type="suggest": values always offered (e.g. common currencies). */
  extra?: string[];
  /** type="suggest": picking a value from a source with ids also sets this field (label → labelId); typing clears it. */
  idField?: string;
  storageFolder?: string;
  /** For type="artist": the field key that should receive the picked artist's
   *  doc id when the user selects from the autocomplete (e.g. "artistId"). */
  artistIdField?: string;
}

interface AdminToolbarProps {
  /** Entity display name (e.g. "Artist", "Event") */
  entityType: string;
  /** Current entity data (key-value record) */
  entityData: Record<string, any>;
  /** Whether entity is currently hidden (adminHidden) */
  isHidden?: boolean;
  /** Fields shown in the edit dialog */
  editFields: AdminField[];
  /** If provided, also show toolbar for the user who owns this content */
  ownerId?: string;
  /** Called to toggle visibility. Return true on success. */
  onToggleVisibility?: (hidden: boolean) => Promise<boolean>;
  /** Called to update entity. Return true on success. */
  onUpdate?: (data: Record<string, any>) => Promise<boolean>;
  /** Called to delete entity. Return true on success. */
  onDelete?: () => Promise<boolean>;
  /** Called after delete succeeds (e.g. redirect) */
  onDeleteRedirect?: () => void;
  /** If provided, the pencil button calls this INSTEAD of opening the
   *  generic editFields-driven dialog below — for entity types (like
   *  Artist) that already have a full, dedicated edit dialog elsewhere on
   *  the page. Without this override the generic dialog only ever shows
   *  the handful of fields listed in editFields, which is why Gordon saw
   *  an "incomplete" edit form missing Gallery/Videos/etc. that the real
   *  editor already has (2026-09-20). */
  onEditClick?: () => void;
  /** Additional CSS class */
  className?: string;
}

/** Inline image field with upload + URL paste */
function ImageFieldEditor({ value, onChange, storageFolder = "admin-uploads" }: { value: string; onChange: (v: string) => void; storageFolder?: string }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const url = await uploadFile(file, `${storageFolder}/img-${Date.now()}.${ext}`);
      onChange(url);
    } catch (err: any) {
      toast({ title: "Upload failed", description: err?.message, variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2 items-center">
        {value && (
          <Image src={value} alt="preview" width={52} height={52}
            className="rounded-md object-cover flex-shrink-0 border border-border w-[52px] h-[52px]" />
        )}
        {!value && (
          <div className="w-[52px] h-[52px] rounded-md border border-dashed border-border flex items-center justify-center flex-shrink-0 bg-muted/30">
            <ImageIcon className="h-5 w-5 text-muted-foreground/40" />
          </div>
        )}
        <div className="flex-1 space-y-1.5">
          <Input value={value} onChange={e => onChange(e.target.value)} placeholder="https://..." className="text-xs h-8" />
          <div className="flex gap-1.5">
            <Button type="button" variant="outline" size="sm" className="text-xs h-7 gap-1"
              onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
              Upload
            </Button>
            {value && (
              <Button type="button" variant="ghost" size="sm" className="text-xs h-7 text-destructive"
                onClick={() => onChange('')}><X className="h-3 w-3" /></Button>
            )}
          </div>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*,image/gif,image/webp" className="hidden" onChange={handleFile} />
    </div>
  );
}

/**
 * Floating admin toolbar for Super Admins.
 * Provides hide/show, edit, and delete controls on any entity detail page.
 * Only renders if the current user is a super admin.
 */
export default function AdminToolbar({
  entityType,
  entityData,
  isHidden = false,
  editFields,
  ownerId,
  onToggleVisibility,
  onUpdate,
  onDelete,
  onDeleteRedirect,
  onEditClick,
  className = "",
}: AdminToolbarProps) {
  const { isSuperAdmin, isAdmin, loading: adminLoading } = useAdmin();
  const { user } = useAuth();
  const { toast } = useToast();

  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editData, setEditData] = useState<Record<string, any>>({});
  const [hiddenState, setHiddenState] = useState(isHidden);

  // Show for super admins, admins, or the content owner
  const isOwner = !!(ownerId && user?.uid && user.uid === ownerId);
  const isAdminLevel = isSuperAdmin || isAdmin;

  // The pencils on covers (Home cards, the track page, the player — Gordon
  // 2026-10-31) open this edit window: in place with a `bb-open-entity-edit`
  // event, or on arrival with ?edit=1.
  const openEditRef = useRef<() => void>(() => {});
  const canUse = !adminLoading && (isAdminLevel || isOwner);
  const entityId = (entityData as any)?.id || (entityData as any)?.workId || "";
  useEffect(() => {
    const h = (e: Event) => {
      const id = (e as CustomEvent).detail?.id;
      if (!id || id === entityId) openEditRef.current();
    };
    window.addEventListener("bb-open-entity-edit", h);
    return () => window.removeEventListener("bb-open-entity-edit", h);
  }, [entityId]);
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!canUse || autoOpened.current) return;
    if (new URLSearchParams(window.location.search).get("edit") !== "1") return;
    autoOpened.current = true;
    openEditRef.current();
  }, [canUse]);

  if (adminLoading || (!isAdminLevel && !isOwner)) return null;

  const openEdit = () => {
    const initial: Record<string, any> = {};
    editFields.forEach(f => {
      initial[f.key] = entityData[f.key] ?? "";
      const empty = initial[f.key] === "" || (Array.isArray(initial[f.key]) && !initial[f.key].length);
      // Older docs keep one genre in `genre`, and the year only inside `releaseDate`.
      if (empty && f.type === "genre" && entityData.genre) initial[f.key] = entityData.genre;
      if (empty && f.type === "year" && /^\d{4}/.test(String(entityData.releaseDate || ""))) initial[f.key] = String(entityData.releaseDate).slice(0, 4);
    });
    setEditData(initial);
    setEditOpen(true);
  };
  openEditRef.current = openEdit;

  const handleToggleVisibility = async () => {
    if (!onToggleVisibility) return;
    setBusy(true);
    try {
      const newHidden = !hiddenState;
      const ok = await onToggleVisibility(newHidden);
      if (ok) {
        setHiddenState(newHidden);
        toast({ title: newHidden ? `${entityType} hidden` : `${entityType} visible` });
      } else {
        toast({ title: "Error", description: "Failed to update visibility", variant: "destructive" });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleUpdate = async () => {
    if (!onUpdate) return;
    setBusy(true);
    try {
      const ok = await onUpdate(editData);
      if (ok) {
        toast({ title: `${entityType} updated` });
        setEditOpen(false);
        // Reload after short delay so Firestore propagates
        setTimeout(() => window.location.reload(), 500);
      } else {
        toast({ title: "Error", description: "Failed to update", variant: "destructive" });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setBusy(true);
    try {
      const ok = await onDelete();
      if (ok) {
        toast({ title: `${entityType} deleted` });
        setDeleteOpen(false);
        onDeleteRedirect?.();
      } else {
        toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating toolbar */}
      <div className={`flex items-center gap-1.5 bg-destructive/10 border border-destructive/30 rounded-lg px-2 py-1.5 ${className}`}>
        {isAdminLevel ? (
          <Badge variant="outline" className="text-[10px] font-semibold border-destructive/40 text-destructive bg-destructive/5 mr-1">
            ADMIN
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] font-semibold border-primary/40 text-primary bg-primary/5 mr-1">
            MINE
          </Badge>
        )}

        {hiddenState && (
          <Badge variant="secondary" className="text-[10px] bg-yellow-500/20 text-yellow-600 border-yellow-500/30">
            HIDDEN
          </Badge>
        )}

        {/* Visibility toggle: admins only */}
        {onToggleVisibility && isAdminLevel && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 hover:bg-destructive/10"
            onClick={handleToggleVisibility}
            disabled={busy}
            title={hiddenState ? "Show" : "Hide"}
          >
            {hiddenState ? <EyeOff className="h-3.5 w-3.5 text-yellow-600" /> : <Eye className="h-3.5 w-3.5" />}
          </Button>
        )}

        {(onUpdate || onEditClick) && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 hover:bg-destructive/10"
            onClick={onEditClick || openEdit}
            disabled={busy}
            title="Edit"
          >
            <Edit3 className="h-3.5 w-3.5" />
          </Button>
        )}

        {onDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 hover:bg-destructive/20 text-destructive"
            onClick={() => setDeleteOpen(true)}
            disabled={busy}
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-xl p-0 flex flex-col gap-0 overflow-hidden max-h-[85vh]">
          <DialogHeader className="px-6 py-4 border-b border-border/40 flex-shrink-0 bg-card">
            <DialogTitle>Edit {entityType}</DialogTitle>
            <DialogDescription>Update the {entityType.toLowerCase()} details below.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {editFields.map(field => (
              <div key={field.key} className="space-y-1.5">
                <Label className="text-sm font-medium">{field.label}</Label>
                {field.type === "select" ? (
                  <Select
                    value={String(editData[field.key] ?? "")}
                    onValueChange={v => setEditData(prev => ({ ...prev, [field.key]: v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {field.options?.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : field.type === "textarea" ? (
                  <textarea
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px]"
                    value={editData[field.key] ?? ""}
                    onChange={e => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                  />
                ) : field.type === "number" ? (
                  <Input
                    type="number"
                    value={editData[field.key] ?? ""}
                    onChange={e => setEditData(prev => ({ ...prev, [field.key]: parseFloat(e.target.value) || 0 }))}
                  />
                ) : field.type === "image" ? (
                  <ImageFieldEditor
                    value={String(editData[field.key] ?? "")}
                    onChange={v => setEditData(prev => ({ ...prev, [field.key]: v }))}
                    storageFolder={field.storageFolder || `admin-uploads/${entityData.id || 'new'}`}
                  />
                ) : field.type === "genre" ? (
                  // Tag-pill genre picker with DB-backed suggestions (searches
                  // the genres collection). editData stays a comma-separated
                  // string so the entity's onUpdate handler is unchanged.
                  <MultiGenreInput
                    values={
                      Array.isArray(editData[field.key])
                        ? (editData[field.key] as string[])
                        : String(editData[field.key] ?? "").split(",").map(s => s.trim()).filter(Boolean)
                    }
                    onChange={(vals) => setEditData(prev => ({ ...prev, [field.key]: vals.join(", ") }))}
                  />
                ) : field.type === "artist" ? (
                  // Autocomplete from the `artists` Firestore collection.
                  // Free-typing is allowed (the field doubles as a name input)
                  // but selecting from the dropdown also drops the artist doc
                  // id into the configured artistIdField so the work links to
                  // the right artist record.
                  <ArtistInput
                    value={String(editData[field.key] ?? "")}
                    onChange={(v) => setEditData(prev => ({ ...prev, [field.key]: v }))}
                    onArtistSelect={(a) => setEditData(prev => ({
                      ...prev,
                      [field.key]: a.name,
                      ...(field.artistIdField ? { [field.artistIdField]: a.id } : {}),
                    }))}
                  />
                ) : field.type === "year" ? (
                  <YearPickerInput
                    value={editData[field.key] ? String(editData[field.key]) : ""}
                    onChange={(y) => setEditData(prev => ({ ...prev, [field.key]: y }))}
                  />
                ) : field.type === "date" ? (
                  <Input
                    type="date"
                    value={String(editData[field.key] ?? "").slice(0, 10)}
                    onChange={e => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                  />
                ) : field.type === "suggest" ? (
                  // Database-driven: offers what's already in BB (Gordon 2026-11-03).
                  <DbSuggestInput
                    value={String(editData[field.key] ?? "")}
                    sources={field.suggest || []}
                    extra={field.extra}
                    onChange={(v) => setEditData(prev => ({ ...prev, [field.key]: v, ...(field.idField ? { [field.idField]: "" } : {}) }))}
                    onPick={(o) => { if (field.idField && o.id) setEditData(prev => ({ ...prev, [field.idField!]: o.id })); }}
                  />
                ) : field.type === "ai_disclosure" ? (
                  <AIDisclosureEditor
                    value={
                      editData[field.key] || {
                        level: editData.aiLevel || "human",
                        aiPercent: editData.aiPercent ?? 0,
                        compositionHuman: editData.compositionHuman ?? true,
                      }
                    }
                    onChange={(d) =>
                      setEditData((prev) => ({
                        ...prev,
                        [field.key]: d,
                        aiLevel: d.level,
                        aiPercent: d.aiPercent,
                        compositionHuman: d.compositionHuman,
                      }))
                    }
                  />
                ) : (
                  <Input
                    value={editData[field.key] ?? ""}
                    onChange={e => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                  />
                )}
              </div>
            ))}
          </div>
          {/* Pinned Sticky Save / Cancel Footer */}
          <div className="sticky bottom-0 bg-background/95 backdrop-blur-md border-t border-border/80 px-6 py-3 flex items-center justify-end gap-2 z-30 flex-shrink-0 shadow-lg mt-auto">
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation — requires typing "I AM SURE" */}
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Delete ${entityType}`}
        itemLabel={entityData?.title || entityData?.name || entityType}
        onConfirm={handleDelete}
      />
    </>
  );
}
